// Simulador de alerta (Editar dispositivo → Alertas, 2026-10-30): a equipe
// manda pro PRÓPRIO celular a mesma notificação que o cliente receberia, pra
// conferir uma reclamação; desde 2026-11-04 também pro celular do cliente. Ver
// simulate-alert.ts no backend.
import { useState } from "react";
import { BellRing, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth-context";
import { alertRulesService, type SimulatedAlertType } from "@/services/alert-rules.service";

const OPTIONS: { type: SimulatedAlertType; label: string }[] = [
  { type: "ignition_on", label: "Ignição ligada" },
  { type: "ignition_off", label: "Ignição desligada" },
  { type: "battery_tamper", label: "Bateria violada" },
  { type: "vibration", label: "Vibração" },
  { type: "anti_theft", label: "Antifurto" },
  { type: "vehicle_blocked", label: "Veículo bloqueado" },
  { type: "vehicle_unblocked", label: "Veículo desbloqueado" },
];

export function SimulateAlertPanel({ deviceId }: { deviceId: number }) {
  const { gpsDriveUser } = useAuth();
  const [type, setType] = useState<SimulatedAlertType>("ignition_on");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  if (gpsDriveUser?.accountType !== "admin" && gpsDriveUser?.accountType !== "funcionario") return null;

  const send = async (target: "me" | "owners") => {
    setSending(true);
    setResult(null);
    try {
      const { apps, browsers } = await alertRulesService.simulate(deviceId, type, target);
      const total = apps + browsers;
      const whose = target === "owners" ? "do cliente" : "seu(s)";
      setResult(
        total === 0
          ? {
              ok: false,
              text:
                target === "owners"
                  ? "O cliente não tem nenhum aparelho registrado. Ele precisa abrir o app com o login dele e permitir notificações."
                  : "Nenhum aparelho seu registrado. Abra o app com o seu login e permita notificações.",
            }
          : {
              ok: true,
              text: `Enviado às ${new Date().toLocaleTimeString("pt-BR")} para ${apps} celular(es) e ${browsers} navegador(es) ${whose}.`,
            },
      );
    } catch (err) {
      setResult({ ok: false, text: err instanceof Error ? err.message : "Não foi possível simular." });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-xl border border-dashed p-3 lg:col-span-2">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <BellRing className="h-4 w-4" /> Simular notificação
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        “Enviar para mim” chega só no seu aparelho. “Enviar para o cliente” chega no celular de quem tem acesso a este veículo — para conferir se o aparelho dele recebe. Nada entra no histórico.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <select
          value={type}
          onChange={(e) => setType(e.target.value as SimulatedAlertType)}
          className="h-9 rounded-lg border bg-background px-2 text-sm"
          aria-label="Tipo de alerta"
        >
          {OPTIONS.map((o) => (
            <option key={o.type} value={o.type}>
              {o.label}
            </option>
          ))}
        </select>
        <Button type="button" size="sm" onClick={() => void send("me")} disabled={sending}>
          {sending && <Loader2 className="h-4 w-4 animate-spin" />} Enviar para mim
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => void send("owners")} disabled={sending}>
          Enviar para o cliente
        </Button>
      </div>
      {result && <p className={`mt-2 text-xs ${result.ok ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600"}`}>{result.text}</p>}
    </div>
  );
}
